import React, { FC, useEffect, useState } from 'react';
import { Select } from 'antd';
import { Spin } from 'common/components';
import { showSuccessMessage } from 'common/helpers';
import { ArrowDownShort } from 'common/svg';
import { EOrderDirection } from 'common/const';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import {
  getCityById,
  getCityList,
  getCityParents,
  selectCity,
  selectCityList,
  selectIsCityLoading,
  setCity,
  updateCity,
} from 'entities/City/City.slice';
import { CityList, CityListPageHeader, CreateCityModal } from 'entities/City/components';
import { cityListIsAvailableOptions, ICityCollectionParams, IUpdateCity } from 'entities/City/City.models';

export const CityListPage: FC = () => {
  const dispatch = useAppDispatch();

  const city = useAppSelector(selectCity);
  const cityList = useAppSelector(selectCityList);
  const isCityLoading = useAppSelector(selectIsCityLoading);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [cityListParams, setCityListParams] = useState<ICityCollectionParams>({ limit: 0, orderDirection: EOrderDirection.ASC });
  const [openCreateCityModal, setOpenCreateCityModal] = useState<boolean>(false);

  const fetchData = async (): Promise<void> => {
    await dispatch(getCityList(cityListParams));
    await dispatch(getCityParents(cityListParams));
    setFetchLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearch = async (value?: string): Promise<void> => {
    const newCityListParams: ICityCollectionParams = { ...cityListParams, name: value };

    setCityListParams(newCityListParams);
    await dispatch(getCityList(newCityListParams));
  };

  const changeIsAvailable = (isAvailable: boolean | string): void => {
    const newCityListParams: ICityCollectionParams = {
      ...cityListParams,
      isAvailable: isAvailable === 'all' ? undefined : isAvailable === true,
    };

    setCityListParams(newCityListParams);
    dispatch(getCityList(newCityListParams));
  };

  const handleOpenCreateCityModal = async (id?: number): Promise<void> => {
    if (id) {
      await dispatch(getCityById(id));
    } else {
      dispatch(setCity(null));
    }

    setOpenCreateCityModal(true);
  };

  const closeCreateCityModal = (): void => {
    setOpenCreateCityModal(false);
  };

  const handleUpdateCity = (payload: IUpdateCity): void => {
    dispatch(
      updateCity({
        ...payload,
        onSuccess: () => {
          payload.onSuccess?.();
          dispatch(setCity(null));
          showSuccessMessage('Город успешно изменен.');
        },
      }),
    );
  };

  if (fetchLoading) {
    return (
      <div className="h-full flex align-center justify-center">
        <Spin />
      </div>
    );
  }

  return (
    <div className="redesign city-list-page">
      <div className="mb-10">
        <CityListPageHeader onCreate={() => handleOpenCreateCityModal()} onSearch={handleSearch} />
      </div>

      <div className="px-11">
        <div className="mb-6" style={{ maxWidth: 250 }}>
          <Select
            rootClassName="redesign"
            suffixIcon={<ArrowDownShort className="icon-dark-grey" />}
            options={cityListIsAvailableOptions}
            value={cityListParams.isAvailable === undefined ? 'all' : cityListParams.isAvailable}
            onChange={changeIsAvailable}
          />
        </div>

        <CityList items={cityList} onUpdate={handleOpenCreateCityModal} />
      </div>

      <CreateCityModal
        open={openCreateCityModal}
        loading={isCityLoading}
        city={city}
        onCancel={closeCreateCityModal}
        onSubmit={handleUpdateCity}
      />
    </div>
  );
};
